// Phase 24 / Plan 24-06 — Render-Section voll-redesigned (EUA-05/06/09).
// Phase 26.1 / Fix #3 — Cross-Device-Retry-Fallback (Backend liefert bookingId
// + availableMollieMethods, wenn der Bucher die Reminder-Mail auf einem anderen
// Geraet oeffnet und der localStorage-Eintrag fehlt).
// Polling-/Retry-/Cache-Logik (Z.1-146) BYTE-IDENTISCH. Neue Imports + State +
// useEffect-Timer + getBookingById-Query oberhalb des Render-Switches ergänzt.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import {
    useInitiateMolliePaymentForBookingFromEndUserAppMutation,
    useGetBookingByIdQuery,
    useGetAvailablePaymentMethodsFromEndUserAppQuery,
} from "../../generated/graphql";
import {
    useMolliePaymentStatusPolling,
    isTerminalStatus,
} from "./hooks/useMolliePaymentStatusPolling";
import {
    useMolliePendingPayment,
    MolliePendingPaymentEntry,
    MOLLIE_PENDING_KEY_PREFIX,
    isExpiredMolliePendingEntry,
} from "../CreateBooking/hooks/useMolliePendingPayment";
import { URL_LOGIN, URL_PAYMENT_RETURN } from "../../models/url";
import { MolliePaymentReturnCard, MolliePaymentReturnCardBooking } from "./MolliePaymentReturnCard";
import { MollieMethodsBlock } from "../CreateBooking/Steps/MollieMethodsBlock";
import { gateMollieMethodsForDevice } from "../CreateBooking/helpers/gateMollieMethodsForDevice";
import { safeStorage } from "../../helpers/safeStorage";
import { embedReturnBaseUrl } from "../../helpers/embedMode";
import { reloadAtRoute } from "../../helpers/reloadAtRoute";
import { rememberAppliedDiscount } from "../CreateBooking/BookingSummary/BookingPricePreview/discountCodeMessages";
import { getGraphQLErrorCode } from "../../helpers/sentry";
import css from "./PaymentReturn.module.css";

interface RouteParams {
    spId: string;
}

function formatMollieMethodForCustomer(method?: string | null): string | null {
    switch (method) {
        case "banktransfer":
            return "Überweisung";
        case "creditcard":
            return "Kreditkarte";
        case "paypal":
            return "PayPal";
        case "directdebit":
            return "Lastschrift";
        default:
            return method || null;
    }
}

function formatPaymentMethodFallback(methodType?: string | null): string {
    switch (methodType) {
        case "BankTransfer":
        case "bankTransfer":
            return "Überweisung";
        case "SepaDebit":
        case "sepaDebit":
            return "Lastschrift";
        case "Cash":
        case "cash":
            return "Bar";
        default:
            return methodType || "";
    }
}

export function PaymentReturn(): JSX.Element {
    const { spId } = useParams<RouteParams>();
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const client = useApolloClient();
    const [initiateMutation] = useInitiateMolliePaymentForBookingFromEndUserAppMutation();

    // No-Attempt-Fall (Konto → "Jetzt bezahlen"): Die SP hatte noch nie einen
    // Mollie-Versuch, daher kein localStorage-Eintrag und status?.bookingId ist
    // null. Die Konto-Seite reicht die bookingId als Query-Param durch, damit die
    // initiate-Mutation den ersten Versuch starten kann.
    const queryBookingId = useMemo(() => new URLSearchParams(location.search).get("bookingId"), [location.search]);
    // Inverse-Race-Marker aus dem Checkout (BookingSummary): Der Kunde wollte sich
    // auf die Warteliste setzen lassen, waehrenddessen wurde ein Platz frei — die
    // Buchung ist regulaer und zahlungspflichtig. Ohne Hinweis waere der Sprung in
    // den Zahlungsschritt fuer ihn unerklaerlich.
    const waitlistPlaceFreed = useMemo(
        () => new URLSearchParams(location.search).get("waitlistPlaceFreed") === "1",
        [location.search]
    );

    // localStorage-Entry (aus 09-01, erweitert um bookingId/method/locale) vor Mount lesen.
    // Kritisch: SOFORT berechnen - kein setState, kein useEffect - damit banktransferSkip
    // deterministisch VOR dem ersten Polling-Tick vorliegt (verhindert Doppel-Query in StrictMode).
    const preMountEntry = useMemo<{
        bookingId: string | null;
        method: string | null;
        locale: string | null;
    }>(() => {
        try {
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (!key || !key.startsWith(MOLLIE_PENDING_KEY_PREFIX)) continue;
                const raw = window.localStorage.getItem(key);
                if (!raw) continue;
                try {
                    const entry = JSON.parse(raw) as Record<string, unknown>;
                    // Abgelaufene Einträge (> 24 h) zählen nicht: Ihr Retry-Kontext ist weg,
                    // sonst bliebe nur ein Retry-Button ohne Methode statt der Methodenwahl.
                    if (
                        entry.scheduledPaymentId === spId &&
                        !isExpiredMolliePendingEntry(entry as Pick<MolliePendingPaymentEntry, "createdAt">)
                    ) {
                        return {
                            bookingId:
                                typeof entry.bookingId === "string"
                                    ? entry.bookingId
                                    : key.substring(MOLLIE_PENDING_KEY_PREFIX.length),
                            method: typeof entry.method === "string" ? entry.method : null,
                            locale: typeof entry.locale === "string" ? entry.locale : null,
                        };
                    }
                } catch {
                    /* skip corrupted */
                }
            }
        } catch {
            /* localStorage not available */
        }
        return { bookingId: null, method: null, locale: null };
    }, [spId]);

    // Banktransfer-Skip DETERMINISTISCH beim Mount: Method aus localStorage-Entry lesen (aus 09-01 Revision-1).
    const isBanktransferFlow = preMountEntry.method === "banktransfer";

    // Dispatch-Zahlungslink-Fall: Der Bucher klickt einen per E-Mail versendeten
    // Mollie-Link und ist NICHT in der EUA eingeloggt. Ohne Login geht der
    // GraphQL-Call unauthentifiziert raus → die `@Authorized`-Query liefert
    // UNAUTHENTICATED. Wir nutzen dann die auth-freie Public-Query.
    // `loggedInUser` ist der projektweit etablierte Login-Indikator (App.tsx,
    // AuthHeader.tsx, BookingSummary.tsx) und wird in allen Login-Flows zusammen
    // mit `accessToken` gesetzt/entfernt — dieselbe Quelle nutzt auch die
    // bestehende Retry-Vorpruefung (handleRetryWithMethod). SOFORT (synchron)
    // beim Mount lesen, damit der Hook deterministisch das richtige Document
    // waehlt (analog zu `isBanktransferFlow`).
    const isLoggedIn = !!safeStorage.getItem("loggedInUser");

    const polling = useMolliePaymentStatusPolling({
        scheduledPaymentId: spId,
        banktransferSkip: isBanktransferFlow,
        usePublicEndpoint: !isLoggedIn,
    });
    const status = polling.data;

    // Phase 26.1 / Fix #3 — Cross-Device-Fallback: Wenn localStorage keinen
    // bookingId fuer dieses spId enthaelt (Bucher oeffnet die Reminder-Mail
    // auf einem anderen Geraet), kommt sie aus dem Backend ueber den
    // erweiterten `MolliePaymentStatus.bookingId`-Field. Der lokal-erst-
    // Fallback-Pfad bleibt unveraendert fuer Backwards-Compat.
    const bookingId: string | null =
        preMountEntry.bookingId ?? queryBookingId ?? status?.bookingId ?? null;

    // Eviction + Cache-Invalidierung bei Terminal
    const pending = useMolliePendingPayment(bookingId);
    const retryContextRef = useRef<MolliePendingPaymentEntry | null>(null);
    useEffect(() => {
        if (status && isTerminalStatus(status.status)) {
            // CR-01 Fix: Retry-Kontext VORHER sichern, bevor pending geleert wird
            retryContextRef.current = pending.get();
            pending.clear();
            if (status.status === "paid") {
                // D-14: Apollo-Cache-Invalidierung + refetch Booking-Queries
                client.cache.evict({ fieldName: "molliePaymentStatusFromEndUserApp" });
                client.cache.gc();
                void client.refetchQueries({
                    include: ["getBookingById"],
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status?.status]);

    // D-12: Retry-Flow fuer failed/canceled/expired
    const [retryLoading, setRetryLoading] = useState<boolean>(false);
    // Der Rabatt steht hier aus der Buchung selbst; der Merker für die Ergebnisseite darf nicht liegen bleiben.
    useEffect(() => {
        rememberAppliedDiscount(null);
    }, []);
    // Reentrancy-Guard: setRetryLoading ist State und flippt NICHT zwischen zwei
    // synchronen Klicks (schnelles Antippen zweier verschiedener Methoden-Karten).
    // Ein Ref blockiert die zweite Invocation sofort → nur EIN initiate/Attempt.
    const retryInFlightRef = useRef<boolean>(false);

    // Phase 26.1 / Fix #3 — Cross-Device-Retry: Bei fehlendem localStorage-
    // Kontext sammelt die Page den vom Bucher gewaehlten Method-Picker-Wert hier.
    const [selectedFallbackMethod, setSelectedFallbackMethod] =
        useState<string | null>(null);

    // Phase 26.1 / Fix #3 — Auth-Check: Die initiate-Mutation ist @Authorized.
    // Wenn der Bucher beim Reminder-Mail-Klick nicht eingeloggt ist, leiten wir
    // ihn vor dem Mutation-Call zum LoginPanel um. Nach erfolgreichem Login
    // bringt der LoginPanel ihn ueber `fromUrl` (LoginPanel.tsx:33, App.tsx:478)
    // wieder auf die PaymentReturn-Page.
    const redirectToLoginWithReturn = useCallback(() => {
        history.push({
            pathname: URL_LOGIN,
            state: { fromUrl: { pathname: `/payment-return/${spId}` } },
        });
    }, [history, spId]);

    /**
     * Phase 26.1 / Fix #3 — Verallgemeinerter Retry: Akzeptiert optional eine
     * vom Bucher gewaehlte Methode (Cross-Device-Fall, wenn die Original-Methode
     * aus localStorage nicht mehr verfuegbar ist).
     *
     * Aufloesungs-Reihenfolge:
     *  1. localStorage-Entry.method (Same-Device-Same-Browser-Pfad, unveraendert)
     *  2. selectedMethodOverride (Cross-Device-Method-Picker, neu)
     *  3. Hard-Fail mit User-Message (statt silent zu URL_BASE redirecten)
     *
     * bookingId aufloesen:
     *  1. localStorage-Entry.bookingId
     *  2. status.bookingId (Backend-Fallback, neu)
     *  3. Hard-Fail
     */
    const handleRetryWithMethod = useCallback(
        async (selectedMethodOverride?: string): Promise<void> => {
            // Auth-Vorpruefung (Reminder-Mail aus ausgeloggtem Browser).
            // `loggedInUser` wird in App.tsx, AuthHeader.tsx, BookingSummary.tsx
            // konsistent als Indikator fuer Login-State verwendet.
            const loggedInUser = safeStorage.getItem("loggedInUser");
            if (!loggedInUser) {
                redirectToLoginWithReturn();
                return;
            }

            // Reentrancy-Guard NACH dem Auth-Check (der Early-Return darf den Ref
            // nicht setzen, sonst blieben spaetere legitime Retries blockiert).
            if (retryInFlightRef.current) return;
            retryInFlightRef.current = true;
            setRetryLoading(true);
            try {
                // retryContextRef wird nur im Terminal-Status-useEffect gesetzt.
                // Beim Banktransfer-Init-Fehler (EUA-BT-01) ist der Status nicht
                // terminal → der Ref bleibt null. Dann faellt der Kontext direkt
                // aus dem (noch vorhandenen) localStorage-Eintrag zurueck, den
                // BookingSummary (writeMolliePendingEntry) vor der fehlgeschlagenen Mutation
                // geschrieben hat (bookingId + method=banktransfer + locale).
                const entry = retryContextRef.current || pending.get();
                const methodForRetry =
                    (entry && entry.method) || selectedMethodOverride || null;
                const bookingIdForRetry =
                    (entry && entry.bookingId) || status?.bookingId || queryBookingId || null;

                if (!bookingIdForRetry || !methodForRetry) {
                    // Hard-Fail mit User-Message — frueher silent-redirect zu
                    // URL_BASE (Phase 24), jetzt explizit, damit der Bucher
                    // weiss warum der Klick nichts macht.
                    message.error(t("mollieCheckout.returnPage.retryContextMissing"));
                    return;
                }

                const scheduledPaymentIdForRetry =
                    (entry && entry.scheduledPaymentId) || spId;
                const localeForRetry = (entry && entry.locale) ?? null;

                const { data } = await initiateMutation({
                    variables: {
                        bookingId: bookingIdForRetry,
                        method: methodForRetry,
                        scheduledPaymentId: scheduledPaymentIdForRetry,
                        locale: localeForRetry,
                        returnBaseUrl: embedReturnBaseUrl(),
                    },
                });
                // EUA-BT-02: Banktransfer erzwingt einen Full-Reload (kein
                // history.push — React-Router v5 remountet nicht, das gestoppte
                // Polling bliebe stehen). bookingId als Query-Param erhalten, damit
                // sie nach dem Reload nicht verloren geht (No-Attempt-Erstversuch aus
                // dem Konto hat keinen localStorage-Eintrag als Fallback). Die Adresse
                // baut der Router (iFrame `#/…`, Embed `?ko=…`), siehe reloadAtRoute.
                const banktransferReturnLocation = {
                    pathname: URL_PAYMENT_RETURN.replace(":spId", scheduledPaymentIdForRetry),
                    search: `?bookingId=${encodeURIComponent(bookingIdForRetry)}`,
                };
                const payload =
                    data && data.initiateMolliePaymentForBookingFromEndUserApp;
                if (payload && payload.checkoutUrl) {
                    if (methodForRetry === "banktransfer") {
                        reloadAtRoute(history, banktransferReturnLocation);
                        return;
                    }
                    window.location.href = payload.checkoutUrl;
                    return;
                }
                if (payload && methodForRetry === "banktransfer") {
                    reloadAtRoute(history, banktransferReturnLocation);
                    return;
                }
                // Mutation lief durch, aber keine checkoutUrl
                message.error(t("mollieCheckout.returnPage.retryError"));
            } catch (err) {
                // Wartelistenplatz: Zahlungsstart wird serverseitig bewusst abgelehnt
                // (Zahlung erst beim Nachrücken) — Wartelisten-Hinweis statt
                // generischem Retry-Fehler.
                if (getGraphQLErrorCode(err) === "WAITLIST_PAYMENT_NOT_ALLOWED") {
                    message.info(
                        t(
                            "mollieCheckout.waitlist.checkoutHint",
                            "Dieser Kurs ist aktuell ausgebucht – du wirst auf die Warteliste gesetzt. Eine Zahlung ist erst nötig, wenn dein Platz zugeteilt wird; du erhältst dann eine E-Mail mit Zahlungslink.",
                        ),
                    );
                    return;
                }
                // eslint-disable-next-line no-console
                console.warn("[PaymentReturn] retry initiate failed", err);
                message.error(t("mollieCheckout.returnPage.retryError"));
            } finally {
                setRetryLoading(false);
                retryInFlightRef.current = false;
            }
        },
        [initiateMutation, spId, t, status, queryBookingId, redirectToLoginWithReturn, pending.get, history]
    );

    // Backwards-Compat-Alias fuer Aufrufer ohne Method-Override (alte
    // Card-Komponenten-Retry-Button + bestehende Tests).
    const handleRetry = useCallback(() => handleRetryWithMethod(), [
        handleRetryWithMethod,
    ]);

    // ============================================================================
    // Plan 24-06 — Neue State/Effekte für Sketch-Variant-D-Return-Page.
    // (Z.1-148 oben: Polling/Retry/Cache-Logik BYTE-IDENTISCH zur Pre-24-06-Version.)
    // ============================================================================

    // D-12: 10s-Pending-Timer-Switch — erste 10s Ant-<Spin>, dann EUA-Hourglass-Card.
    const [showFullPendingCard, setShowFullPendingCard] = useState<boolean>(false);
    useEffect(() => {
        // Timer nur starten, wenn wir tatsächlich pollen UND noch keinen Status haben.
        if (!polling.loading || status) {
            setShowFullPendingCard(false);
            return;
        }
        const timerId = setTimeout(() => setShowFullPendingCard(true), 10000);
        // React 18 StrictMode-safe Cleanup — verhindert Doppel-Timer im Dev-Mode.
        return () => clearTimeout(timerId);
    }, [polling.loading, status]);

    // D-08: getBookingById-Query liefert Order-Box-Daten (Buchungsnummer, Kurs,
    // Teilnehmer, Betrag, Methode, Bezahlt am). Skip, wenn keine bookingId
    // (z.B. localStorage-Eintrag verloren oder erster Aufruf ohne pending entry).
    const bookingQuery = useGetBookingByIdQuery({
        variables: { id: bookingId ?? "" },
        skip: !bookingId,
        fetchPolicy: "cache-first",
    });
    const bookingQueryData = bookingQuery?.data;

    const bookingForCard = useMemo<MolliePaymentReturnCardBooking | null>(() => {
        const b = bookingQueryData?.bookingById;
        if (!b) return null;
        const scheduledPayments = b.scheduledPayments || [];
        const scheduledPaymentForReturn =
            scheduledPayments.find((sp) => sp.id === spId) ||
            scheduledPayments.find((sp) => Boolean(sp.mollieMethod));
        const mollieMethodLabel = formatMollieMethodForCustomer(
            (scheduledPaymentForReturn && scheduledPaymentForReturn.mollieMethod) ||
            preMountEntry.method,
        );
        const courseTypeName = b.course?.courseType?.name ?? "";
        const coursePrefix = b.course?.prefixedCourseNumber ?? "";
        const courseName = courseTypeName
            ? `${courseTypeName}${coursePrefix ? ` (${coursePrefix})` : ""}`
            : coursePrefix;
        return {
            bookingNumber: `${b.bookingNumberPrefix ?? ""}${b.bookingNumber}`,
            attendeeFullName: `${b.attendee?.firstname ?? ""} ${b.attendee?.lastname ?? ""}`.trim(),
            courseName,
            grossPrice: b.grossPrice ?? 0,
            paymentMethodLabel:
                mollieMethodLabel || formatPaymentMethodFallback(b.paymentMethod?.type),
            paidAt: b.payDate ? new Date(b.payDate) : null,
            discount: b.discount,
            discountName: b.discountName,
            // bookerEmail nicht in EUA-Schema-Selection-Set verfügbar (Plan-Diskussion W-7) —
            // emailHint wird nur conditional gerendert (booking.bookerEmail === undefined → kein Hint).
            bookerEmail: undefined,
        };
    }, [bookingQueryData, preMountEntry.method, spId]);

    // EUA-BT-01: Banktransfer-Init-Fehler-Sackgasse. Schlaegt `createPayment`
    // NACH der bereits angelegten Buchung fehl (v.a. "banktransfer nicht im
    // Mollie-Profil aktiv" = 422, oder transient), landet der Bucher ueber den
    // catch-Fallback in BookingSummary auf dieser Return-Seite — aber
    // ohne `bankTransferDetails`, weil gar keine Mollie-Zahlung zustande kam.
    // Das (Banktransfer-One-Shot-)Polling ist dann gestoppt; die generische
    // Pending-Card ("wird automatisch aktualisiert") waere irrefuehrend und ein
    // Dead-End. Stattdessen behandeln wir diesen Zustand wie einen
    // fehlgeschlagenen Versuch und bieten den vorhandenen Retry-Ausweg an.
    //
    // Praezise + Doppelzahlungs-sicher: NUR wenn entweder gar kein Status vorliegt
    // ODER ein "open"-Payment ohne Ueberweisungsdaten (unbrauchbar). "pending"/
    // "paid"/Terminal-Status werden NICHT angefasst — dort existiert die Zahlung
    // bereits.
    const isBanktransferInitFailure =
        isBanktransferFlow &&
        !polling.loading &&
        !polling.isTimeout &&
        (!status || (status.status === "open" && !status.bankTransferDetails));

    // No-Attempt-Fall (Konto → "Jetzt bezahlen"): Die SP hatte noch nie einen
    // Mollie-Versuch. `molliePaymentStatusFromEndUserApp` liefert dann null (kein
    // Attempt / keine molliePaymentId), das Polling laeuft leer bis Timeout. Statt
    // der irrefuehrenden "wird bearbeitet"-Card bieten wir direkt die Methodenwahl
    // an (initiate startet den ersten Versuch → Redirect zum Mollie-Checkout).
    // Voraussetzung: eingeloggt + bookingId vorhanden (aus dem Konto-Query-Param),
    // sonst kann die initiate-Mutation nicht laufen. Banktransfer-Init-Fehler hat
    // seinen eigenen (retrybaren) Branch → hier ausgeschlossen.
    // Picker-Gate fuer BEIDE Branches (Terminal-Retry + No-Attempt) aus DERSELBEN
    // Quelle wie das rendernde Child (MollieMethodsBlock nutzt
    // getAvailablePaymentMethodsFromEndUserApp, cache-first → geteilter Cache-Key,
    // kein Extra-Request). Nur so koennen Parent-Gate und Child-Render nicht
    // divergieren: status.availableMollieMethods filtert NICHT nach
    // mollieEnabled-Flag / booker.sepaDisabled (directdebit), die Child-Query schon
    // — sonst rendert das Child null waehrend der Retry-Button unterdrueckt ist
    // (Sackgasse). `skip` haelt die @Authorized-Query von ausgeloggten
    // Dispatch-Link-Buchern fern.
    const methodsQuery = useGetAvailablePaymentMethodsFromEndUserAppQuery({
        fetchPolicy: "cache-first",
        skip: !isLoggedIn || !bookingId,
    });
    const hasDeviceUsableMethod =
        gateMollieMethodsForDevice(
            methodsQuery?.data?.getAvailablePaymentMethodsFromEndUserApp,
        ).length > 0;
    // Picker nur SUPPRIMIEREN, wenn die Query definitiv leer/gefiltert zurueckkam.
    // Waehrend loading (Child zeigt Skeleton) und bei error (Vor-Fix-Verhalten:
    // Child regelt Empty/Error selbst) optimistisch offen halten — sonst faellt der
    // Nutzer in die statische Pending-Card (Flicker bzw. dauerhafte Sackgasse).
    const methodPickerEligible =
        !!methodsQuery?.loading || !!methodsQuery?.error || hasDeviceUsableMethod;

    const isNoAttemptChoosePayment =
        isLoggedIn &&
        !isBanktransferFlow &&
        !polling.loading &&
        !status &&
        !!bookingId &&
        methodPickerEligible;

    // ============================================================================
    // Render-Switch (D-09: Polling-Logic preserved oben, nur UI-Layer ausgetauscht).
    // ============================================================================

    // Zugriff verweigert: Ein FREMDER Bucher (nicht Eigentuemer der Zahlung) ist
    // eingeloggt → der `@Authorized`-Endpoint wirft ForbiddenError, der Hook stoppt
    // das Polling und setzt accessDenied. Statt endlos "in Bearbeitung" (bis
    // 60s-Timeout) zeigen wir eine klare Kein-Zugriff-Card mit Konto-Wechsel.
    // GANZ OBEN im Switch — vor dem banktransfer-init-failure-Branch, dessen
    // `!status`-Bedingung hier sonst faelschlich greifen wuerde.
    if (polling.accessDenied) {
        return (
            <div className={css.page} aria-live="polite">
                <MolliePaymentReturnCard
                    state="accessDenied"
                    onLoginToPay={redirectToLoginWithReturn}
                />
            </div>
        );
    }

    // D-13: Banktransfer-Pfad (Status "open" ODER "pending") → direkt
    // Card-Pending-State mit eingebetteter BankTransferDetailsSection (KEIN
    // 10s-Spin-Wait, weil Banktransfer nie sofort "paid" ist).
    //
    // "pending" tritt auf, wenn der Bucher die Ueberweisung bereits ausgeloest
    // hat und auf die Return-Page zurueckkehrt (Mollie meldet die eingehende
    // Zahlung als "pending"). Auch dann muessen die Ueberweisungsdaten weiterhin
    // sichtbar bleiben — sonst verliert der Bucher Empfaenger/IBAN/Verwendungszweck.
    if (
        status &&
        (status.status === "open" || status.status === "pending") &&
        status.bankTransferDetails
    ) {
        return (
            <div className={css.page}>
                <MolliePaymentReturnCard
                    state="pending"
                    status={status}
                    booking={bookingForCard}
                />
            </div>
        );
    }

    // D-12: Pending — erste 10s Ant-<Spin>, danach EUA-Hourglass-Card.
    if (polling.loading && !status) {
        if (!showFullPendingCard) {
            return (
                <div className={css.page} aria-live="polite">
                    <Spin size="large" tip={t("mollieCheckout.returnPage.polling.text")} />
                </div>
            );
        }
        return (
            <div className={css.page} aria-live="polite">
                <MolliePaymentReturnCard state="pending" />
            </div>
        );
    }

    // No-Attempt-Fall: Methodenwahl direkt anbieten (VOR Timeout-Branch, damit die
    // Card nicht nach 60s Leerlauf-Polling faelschlich zur Timeout-Card wird). Der
    // Method-Picker triggert bei Auswahl handleRetryWithMethod → initiate → Redirect.
    if (isNoAttemptChoosePayment) {
        return (
            <div className={css.page} aria-live="polite">
                <MolliePaymentReturnCard
                    state="choosePayment"
                    booking={bookingForCard}
                    notice={
                        waitlistPlaceFreed
                            ? t(
                                  "mollieCheckout.returnPage.choosePaymentCard.placeFreedNotice",
                                  "Gute Nachricht: In diesem Kurs ist gerade ein Platz frei geworden. Sie stehen deshalb nicht auf der Warteliste — der Platz ist für Sie gebucht und wartet nur noch auf die Zahlung.",
                              )
                            : undefined
                    }
                    methodPicker={
                        <MollieMethodsBlock
                            selectedMethod={selectedFallbackMethod}
                            onMethodSelect={(methodId) => {
                                setSelectedFallbackMethod(methodId);
                                if (methodId) {
                                    void handleRetryWithMethod(methodId);
                                }
                            }}
                            busy={retryLoading}
                        />
                    }
                />
            </div>
        );
    }

    // Timeout (60s ohne Terminal).
    if (polling.isTimeout && (!status || !isTerminalStatus(status.status))) {
        return (
            <div className={css.page} aria-live="polite">
                <MolliePaymentReturnCard
                    state="timeout"
                    status={status}
                    onManualRefetch={() => void polling.manualRefetch()}
                />
            </div>
        );
    }

    // Terminal-Status: success / failure — ODER der Banktransfer-Init-Fehler
    // (EUA-BT-01), der als (retrybare) Failure-Card mit demselben Ausweg gerendert
    // wird. isBanktransferInitFailure ist per Definition false bei Terminal-Status,
    // daher kein Overlap.
    if ((status && isTerminalStatus(status.status)) || isBanktransferInitFailure) {
        const cardState: "success" | "failure" =
            status && status.status === "paid" ? "success" : "failure";
        const isRetryable =
            isBanktransferInitFailure ||
            status?.status === "failed" ||
            status?.status === "canceled" ||
            status?.status === "expired";

        // Phase 26.1 / Fix #3 — Cross-Device-Method-Picker.
        // Wenn der localStorage-Kontext fehlt UND das Backend uns
        // availableMollieMethods liefert, rendern wir den Picker statt eines
        // direkten Retry-Buttons. Damit funktioniert der Reminder-Mail-Klick
        // auch auf einem fremden Geraet / nach >24h, wenn der LocalStorage-
        // Eintrag verfallen ist.
        //
        // KRITISCH: `hasLocalMethod` muss aus dem SYNCHRON-VOR-MOUNT gelesenen
        // `preMountEntry.method` kommen, NICHT aus `retryContextRef.current`
        // — der Ref wird erst im post-render-useEffect gesetzt und triggert
        // kein Re-Render. Beim ersten Render mit lokalem Eintrag waere der
        // Picker sonst kurz sichtbar (oder dauerhaft, weil der ref-Update
        // kein Re-Render ausloest).
        const hasLocalMethod = !!preMountEntry.method;
        // Retry/Method-Picker sind auth-pflichtige Features (initiate-Mutation ist
        // @Authorized, der Picker triggert sie direkt). Fuer ausgeloggte Bucher aus
        // dem Dispatch-Zahlungslink (Public-Endpoint) blenden wir sie aus.
        // `methodPickerEligible` stammt aus DERSELBEN Query wie das rendernde Child
        // (nicht aus status.availableMollieMethods) → Parent-Gate und Child-Render
        // koennen nicht ueber mollieEnabled/sepaDisabled/Device divergieren.
        const shouldShowMethodPicker =
            isLoggedIn && isRetryable && !hasLocalMethod && methodPickerEligible && !!bookingId;
        const shouldShowRetryButton = isLoggedIn && isRetryable && !shouldShowMethodPicker;

        // Dispatch-Zahlungslink / ausgeloggter Bucher: Retry/Method-Picker sind
        // auth-pflichtig (initiate-Mutation ist @Authorized) und die Public-Query
        // liefert weder bookingId noch availableMollieMethods. Frueher war das ein
        // Sackgassen-Zustand (nur "Später bezahlen", das nichts bezahlte). Jetzt
        // bieten wir dem ausgeloggten Bucher einen primaeren "Anmelden & jetzt
        // bezahlen"-Button an: Er wird ueber `fromUrl` nach dem Login wieder auf
        // DIESE Return-Page geleitet (siehe redirectToLoginWithReturn). Dort ist
        // `isLoggedIn` dann true → der bestehende Method-Picker/Retry-Pfad greift.
        const shouldShowLoginToPay = !isLoggedIn && isRetryable;

        // Optionaler Kurs-Context-Header — gibt dem Bucher Kontext, was er
        // bezahlt, wenn die volle bookingForCard-Resolution nicht greift.
        const courseContext =
            !bookingForCard && status && (status.courseTitle || status.courseStartDate)
                ? {
                      courseTitle: status.courseTitle ?? null,
                      courseStartDate: status.courseStartDate
                          ? new Date(status.courseStartDate)
                          : null,
                  }
                : null;

        const methodPickerNode = shouldShowMethodPicker ? (
            <MollieMethodsBlock
                selectedMethod={selectedFallbackMethod}
                onMethodSelect={(methodId) => {
                    setSelectedFallbackMethod(methodId);
                    // Direkt nach der Auswahl den Retry triggern — der
                    // Bucher hat in der Reminder-Mail bereits explizit "neu
                    // bezahlen" geklickt, ein zusaetzlicher Submit-Schritt
                    // ist UX-Reibung — wir rufen direkt auf.
                    if (methodId) {
                        void handleRetryWithMethod(methodId);
                    }
                }}
                busy={retryLoading}
            />
        ) : null;

        return (
            <div className={css.page} aria-live="polite">
                <MolliePaymentReturnCard
                    state={cardState}
                    status={status}
                    booking={bookingForCard}
                    courseContext={courseContext}
                    onRetry={shouldShowRetryButton ? handleRetry : undefined}
                    retryLoading={retryLoading}
                    methodPicker={methodPickerNode}
                    onLoginToPay={
                        shouldShowLoginToPay ? redirectToLoginWithReturn : undefined
                    }
                />
            </div>
        );
    }

    // Fallback: noch pollend, aber Spin-Phase ist über → Pending-Card. Falls
    // bereits ein (nicht-terminaler) Status mit Überweisungsdaten vorliegt,
    // reichen wir ihn durch, damit der Bucher seine Bankdaten nicht verliert
    // (z.B. Banktransfer-"pending" ohne lokalen localStorage-Kontext).
    return (
        <div className={css.page} aria-live="polite">
            <MolliePaymentReturnCard
                state="pending"
                status={status}
                booking={bookingForCard}
            />
        </div>
    );
}
